import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { usePolling } from '@backoffice-frontend/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useVehiclesOperationalStateQuery } from './graphql/VehiclesOperationalState.hook'
import { allVehiclesAreNonOperational } from './utils'

const POLL_INTERVAL_MS = 1000
const MAX_POLL_ATTEMPTS = 30

type UseVehiclesNonOperationalPollingArgs = {
  onAllNonOperational: () => void
  onTimeout: () => void
}

/**
 * The backend has no mutation that sets the operational state directly: a
 * vehicle becomes non-operational as a consequence of a critical work order
 * being registered against it, which reaches the fleet service asynchronously.
 * So after creating the work order we poll here until the change is visible,
 * rather than claiming success straight away.
 */
export const useVehiclesNonOperationalPolling = ({
  onAllNonOperational,
  onTimeout,
}: UseVehiclesNonOperationalPollingArgs) => {
  const [polledVehicleIds, setPolledVehicleIds] = useState<string[] | null>(
    null,
  )
  const attemptsRef = useRef(0)

  const isPolling = polledVehicleIds !== null

  const { data, refetch } = useErrorHandlingQuery(
    useVehiclesOperationalStateQuery,
    {
      variables: { ids: polledVehicleIds ?? [] },
      skip: !isPolling,
      fetchPolicy: 'network-only',
    },
  )

  const stopPolling = useCallback(() => {
    setPolledVehicleIds(null)
    attemptsRef.current = 0
  }, [])

  const startPolling = useCallback((vehicleIds: string[]) => {
    attemptsRef.current = 0
    setPolledVehicleIds(vehicleIds)
  }, [])

  const poll = useCallback(() => {
    attemptsRef.current += 1

    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
      stopPolling()
      onTimeout()
      return
    }

    void refetch()
  }, [refetch, stopPolling, onTimeout])

  usePolling(poll, POLL_INTERVAL_MS, isPolling)

  useEffect(() => {
    if (!isPolling || !polledVehicleIds || !data) return

    if (allVehiclesAreNonOperational(data.vehiclesByIDs, polledVehicleIds)) {
      stopPolling()
      onAllNonOperational()
    }
  }, [data, isPolling, polledVehicleIds, stopPolling, onAllNonOperational])

  return { startPolling, isPolling }
}
